import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button, Card, Input } from '../../components/ui'
import { Footer } from '../../components/Footer'
import { GhostMark } from '../../components/GhostMark'
import { PasskeyButton } from '../../components/PasskeyButton'
import { GoogleGlyph, MicrosoftGlyph } from '../../components/icons'
import { useStore } from '../../lib/store'
import './auth.css'

// SSO is a later pass; passkeys and email+password are both live.
const SOON = 'SSO is coming soon — use a passkey or your email and password for now.'

export function Login() {
  const { login, loginWithPasskey } = useStore()
  const nav = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [loading, setLoading] = useState(false)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!email.trim() || !password) {
      setError('Enter your email and password.')
      return
    }
    setError('')
    setNotice('')
    setLoading(true)
    try {
      await login(email, password)
      nav('/candidates')
    } catch (err) {
      // Distinguish a real auth rejection (400) from not being able to reach
      // PocketBase at all (status 0 / network error), so a connectivity problem
      // doesn't masquerade as a wrong password.
      const status = (err as { status?: number })?.status
      setError(
        status === 400
          ? 'Those credentials didn’t match. Check your email and password.'
          : 'Couldn’t reach the server. Check your connection and try again.',
      )
      setLoading(false)
    }
  }

  return (
    <div className="auth">
      <Card className="auth__card">
        <div className="auth__logo">
          <GhostMark size={64} glow breathing />
        </div>
        <h1 className="auth__title">Welcome back</h1>
        <p className="auth__sub">Sign in to your recruiter console.</p>

        <form className="auth__form" onSubmit={onSubmit} noValidate>
          <PasskeyButton
            label="Sign in with a passkey"
            onActivate={loginWithPasskey}
            onDone={() => nav('/candidates')}
          />

          <div className="auth__divider">or</div>

          <div className="auth__sso">
            <button type="button" className="sso-btn sso-btn--secondary" onClick={() => setNotice(SOON)}>
              <GoogleGlyph /> Continue with Google
            </button>
            <button type="button" className="sso-btn sso-btn--secondary" onClick={() => setNotice(SOON)}>
              <MicrosoftGlyph /> Continue with Microsoft
            </button>
          </div>

          <div className="auth__divider">email</div>

          <Input
            id="login-email"
            label="Work email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              if (error) setError('')
            }}
          />

          <Input
            id="login-password"
            label="Password"
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value)
              if (error) setError('')
            }}
            error={error}
          />

          {notice && <p className="auth__note">{notice}</p>}

          <Button type="submit" variant="primary" size="lg" block loading={loading}>
            Sign in
          </Button>
        </form>

        <p className="auth__foot">
          New to Vouchr? <Link to="/register">Create an account</Link>
        </p>
      </Card>
      <Footer />
    </div>
  )
}
